import {RefObject, useEffect, useState} from 'react';

import {createPortal} from 'react-dom';
import styled from 'styled-components';

import ImageWithAlt from '~/shared/components/ImageWithAlt';
import {media} from '~/shared/theme/media';

import confettiBurst from './assets/confetti.gif';

/* 90 frames at 20ms. The asset carries no loop block, so the browser plays it through once on its
   own and then holds the last frame - this is when to take that frame away, with a margin for a slow
   first decode. */
const BURST_MS = 1800;
const TEARDOWN_MS = BURST_MS + 1200;

/* The burst has to spill outside the dialog it celebrates, and it cannot do that from inside one:
   the shared modal frame clips its overflow to keep its rounded corners, and the bottom sheet clips
   its own because it scrolls. So it is portalled out and placed against the viewport instead.

   It goes to the body rather than to `#modals`, where the modals themselves portal to: the global
   `* { position: relative }` leaves that element positioned, so its `z-index: 1000` makes it a
   stacking context, and anything inside it is capped at 1000 - under the bottom sheet's 1003, which
   is rendered outside it. Sitting on the body instead is what lets the burst clear both dialogs and
   the dimmed backdrop they lay over the page. */
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${({theme}) => theme.zIndex.modalDecoration};

  /* Keeps the oversized burst from reaching past the viewport and raising a scrollbar. */
  overflow: hidden;

  /* Decoration only - the dialog's own close button underneath stays clickable. */
  pointer-events: none;

  /* A gif cannot be stopped from CSS, so the whole burst goes rather than just its motion. */
  @media (prefers-reduced-motion: reduce) {
    display: none;
  }

  body.contrast-filter & {
    display: none;
  }
`;

/* Square asset, and the confetti inside it keeps to the middle of the square - so it is centred on
   the dialog's top edge, which is where the design has the burst breaking over it, and sized well
   past the dialog so the pieces carry beyond its sides. */
const Burst = styled(ImageWithAlt)<{$top: number; $centerX: number}>`
  position: absolute;
  top: ${({$top}) => $top}px;
  left: ${({$centerX}) => $centerX}px;
  width: 120vw;
  height: 120vw;
  transform: translate(-50%, -50%);
  ${media.minLargeMobile`
    width: 660px;
    height: 660px;
  `}
`;

/* Where the burst is centred: the middle of the dialog's top edge, in viewport coordinates. */
type Anchor = {top: number; centerX: number};

type ConvertLoyaltyBalanceConfettiProps = {
  /* The dialog to break over. Measured rather than assumed, because the two layouts put it in
     different places - the modal is centred in the viewport, the sheet is pinned to its bottom. */
  anchorRef: RefObject<HTMLElement>;
};

/* Plays the celebration burst across the whole screen, once, as the success screen arrives. */
const ConvertLoyaltyBalanceConfetti = ({anchorRef}: ConvertLoyaltyBalanceConfettiProps) => {
  const [anchor, setAnchor] = useState<Anchor | null>(null);

  useEffect(() => {
    const dialog = anchorRef.current;

    if (!dialog) {
      return undefined;
    }

    const {top, left, width} = dialog.getBoundingClientRect();
    setAnchor({top, centerX: left + (width / 2)});

    const timeout = setTimeout(() => setAnchor(null), TEARDOWN_MS);
    return () => clearTimeout(timeout);
  }, [anchorRef]);

  if (!anchor) {
    return null;
  }

  return createPortal(
    <Overlay aria-hidden data-test-id="convertLoyaltyBalanceModal_confetti">
      <Burst src={confettiBurst} noAlt $top={anchor.top} $centerX={anchor.centerX} />
    </Overlay>,
    document.body,
  );
};

export default ConvertLoyaltyBalanceConfetti;
